import { ChevronRightIcon, EllipsisIcon, Loader2Icon, XIcon, type LucideIcon } from "lucide-react"
import { useEffect, useId, useRef, useState, type FocusEvent, type ReactNode } from "react"
import { Button } from "@/primitives/button.tsx"
import { Dialog, DialogClose, DialogContent, DialogTitle } from "@/primitives/dialog.tsx"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/primitives/dropdown-menu.tsx"
import { Input } from "@/primitives/input.tsx"
import { Switch } from "@/primitives/switch.tsx"
import { cn } from "@/primitives/utils"
import { useShortcut } from "@/shortcuts/use-shortcut.ts"
import { composedOf, type Made } from "./composer.ts"
import { DestructiveConfirmation } from "./destructive-confirmation.tsx"
import { useEdgeFade } from "./edge-fade.ts"
import { EditorFillsScreen } from "./editor-sheet.tsx"
import { IdentityTile } from "./identity.tsx"
import { Kbd } from "./key.tsx"
import { PickersReturnFocusTo } from "./property.tsx"
import { useUnsavedWork } from "./unsaved-work.ts"
import { useOverlayForm } from "./use-overlay-form.ts"
import { typesText } from "./viewport.ts"

/*
 * The dialog every new thing is made in: a ticket, an Epic, a Project, a Chat channel. It opens
 * high on the page, says where the new thing goes (the Project's tile and key, then what is being
 * made), and is mostly its title, typed large, with the focus already in it. Beneath the title come the page's own
 * fields (`body`, such as a bare MarkdownEditor for the description), then its property chips
 * (`chips`, PropertyChips opening Pickers).
 *
 * ⌘↵ submits from anywhere in it, once `canSubmit`; so does Enter in the title of one that is
 * a title alone (no `body`), as a channel's name is. With `createMore`, the page keeps it open after
 * a submit and clears the title, and the focus goes back to the title for the next one. A refusal
 * about the title shows under it (`titleError`); the page toasts any other. A line about the title
 * as typed (`titleHint`, what a channel's name becomes) shows there too while nothing is refused:
 * a line, so the dialog grows by no more than that as it comes and goes. While anything is
 * `written` in it, it holds unsaved work.
 *
 * On `compact` it is a full screen instead, chosen as it opens and kept until it closes: Cancel,
 * the crumb and Create along the top, then the title and the body, and the chips in a row at the
 * screen's foot, on the on-screen keyboard, scrolling sideways under the `footerStart` line.
 * Create reads "Create" there, since the crumb says what is made, and shows no ⌘↵ hint; "Create
 * more" is in a ⋯ beside it. A MarkdownEditor in `body` fills the screen, its formatting row on
 * the chips while it has focus. A Picker closed hands the focus back to the field last written in,
 * the title or the body, so the keyboard comes back. Closing it on something written, by Cancel,
 * Back, Esc or a swipe, asks first after the thing it `makes`, which was never created ("Discard
 * this ticket?"); nothing closes it while a create is under way.
 */
export function Composer({
  open,
  onOpenChange,
  crumb,
  titlePlaceholder,
  title,
  onTitle,
  titleError,
  titleHint,
  body,
  chips,
  footerStart,
  submitLabel,
  onSubmit,
  pending,
  canSubmit,
  written,
  createMore,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * Where the new thing goes, and what it is: a Project's key, or an icon for something made outside
   * one, then the label `makes` gives it ("New Epic"). With both, the icon follows the key and says
   * what kind of thing is made in it, as an Epic's bolt.
   */
  crumb: { projectKey?: string; icon?: LucideIcon; makes: Made }
  titlePlaceholder: string
  title: string
  onTitle: (title: string) => void
  titleError?: string
  titleHint?: string
  body?: ReactNode
  chips?: ReactNode
  /** A line on what happens next, such as where a new ticket lands. */
  footerStart?: string
  submitLabel: string
  onSubmit: () => void
  pending: boolean
  canSubmit: boolean
  /** Whether anything is written in it (the title, or the page's own fields), to lose on closing. */
  written: boolean
  createMore?: { checked: boolean; onChange: (checked: boolean) => void }
}) {
  const popup = useRef<HTMLDivElement>(null)
  const titleField = useRef<HTMLInputElement>(null)
  // The field last written in on the full screen, which a Picker gives the focus back to.
  const lastField = useRef<HTMLElement>(null)
  const noteField = (event: FocusEvent<HTMLElement>) => {
    if (typesText(event.target)) lastField.current = event.target
  }
  const returnFocus = () =>
    lastField.current?.isConnected ? lastField.current : titleField.current
  const { label, discards } = composedOf(crumb.makes)
  const errorId = useId()
  const underTitle = titleError ?? titleHint
  const submittable = canSubmit && !pending
  // On compact it is a full screen, as its Dialog chooses: decided as it opens and kept while open.
  const { docked } = useOverlayForm({ open })
  const [asking, setAsking] = useState(false)
  const chipRow = useEdgeFade()
  // What is on its way to being created is no longer at risk, so opening what was made asks nothing.
  useUnsavedWork(open && written && !pending)

  useShortcut(
    "submit",
    () => {
      if (submittable) onSubmit()
    },
    { within: popup, enabled: open },
  )

  // After a submit that keeps the composer open for the next one, typing starts at the title.
  const wasPending = useRef(pending)
  useEffect(() => {
    if (wasPending.current && !pending && open && title === "") titleField.current?.focus()
    wasPending.current = pending
  }, [pending, open, title])

  // The crumb before its label: the Project's tile and key, and what kind of thing is made.
  const lead = (
    <>
      {crumb.projectKey !== undefined && (
        <>
          <IdentityTile projectKey={crumb.projectKey} size={16} />
          <span className="font-medium">{crumb.projectKey}</span>
          <ChevronRightIcon aria-hidden className="size-3 text-subtle-foreground" />
        </>
      )}
      {/* After a Project's key, an icon says what kind of thing is made in it: an Epic's bolt. */}
      {crumb.icon && <crumb.icon aria-hidden className="size-3.5" />}
    </>
  )

  const titled = (
    <div className="space-y-1">
      <Input
        ref={titleField}
        variant="bare"
        aria-label={titlePlaceholder}
        aria-invalid={titleError ? true : undefined}
        aria-describedby={underTitle ? errorId : undefined}
        placeholder={titlePlaceholder}
        value={title}
        onChange={(event) => onTitle(event.target.value)}
        onKeyDown={(event) => {
          // ⌘↵ is the submit shortcut's own; a plain Enter here is this one.
          const plain = !(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
          if (body || event.key !== "Enter" || !plain || event.nativeEvent.isComposing) return
          event.preventDefault()
          if (submittable) onSubmit()
        }}
        className="text-heading font-semibold"
      />
      {underTitle && (
        <p
          id={errorId}
          className={cn("text-xs", titleError ? "text-destructive" : "text-muted-foreground")}
        >
          {underTitle}
        </p>
      )}
    </div>
  )

  // Closing the full screen on something written asks first, however it closes. Back while it
  // asks answers Cancel, and nothing closes it while a create is under way.
  const close = () => {
    if (pending) return
    if (asking) setAsking(false)
    else if (written) setAsking(true)
    else onOpenChange(false)
  }

  if (docked)
    return (
      <>
        <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
          <DialogContent
            ref={popup}
            position="top"
            size="lg"
            fullScreen
            showCloseButton={false}
            initialFocus={titleField}
          >
            {/* A press on its bar or its chips keeps the focus where it is typed, so the keyboard
                stays up and the screen, which rides it, holds still under the finger: a screen
                that dropped as the field let go would take the press's end somewhere else. */}
            <div
              onMouseDown={keepFocus}
              className="sticky top-0 z-10 flex h-12 shrink-0 items-center gap-1 border-b border-border-subtle bg-background px-2"
            >
              <Button variant="ghost" size="sm" disabled={pending} onClick={close}>
                Cancel
              </Button>
              <DialogTitle className="flex min-w-0 flex-1 items-center justify-center gap-1.5 text-ui font-normal text-muted-foreground">
                {lead}
                <span className="truncate font-medium text-foreground">{label}</span>
              </DialogTitle>
              {createMore && (
                <DropdownMenu>
                  <DropdownMenuTrigger
                    render={<Button variant="ghost" size="icon-sm" aria-label="More options" />}
                  >
                    <EllipsisIcon />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuCheckboxItem
                      checked={createMore.checked}
                      onCheckedChange={createMore.onChange}
                      closeOnClick
                    >
                      Create more
                    </DropdownMenuCheckboxItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              <Button
                size="sm"
                disabled={!submittable}
                focusableWhenDisabled={pending}
                onClick={onSubmit}
              >
                {pending && <Loader2Icon aria-hidden className="animate-spin" />}
                Create
              </Button>
            </div>

            <div onFocus={noteField} className="shrink-0 px-4 pt-4 pb-2">
              {titled}
            </div>
            {body && (
              // A MarkdownEditor here fills the screen to its edges, as in an EditorSheet, and its
              // formatting row stands on the chips while it is written in (`markdown.css`).
              <EditorFillsScreen>
                <div
                  data-composer-body=""
                  onFocus={noteField}
                  className="flex min-h-0 flex-1 flex-col px-4 pb-3 text-prose has-[.markdown-editor-frame]:px-0 has-[.markdown-editor-frame]:pb-0"
                >
                  {body}
                </div>
              </EditorFillsScreen>
            )}

            {(chips || footerStart) && (
              // On the screen's foot, which stands on the keyboard, under content of any length.
              <div className="sticky bottom-0 mt-auto shrink-0 border-t border-border-subtle bg-background">
                {footerStart && (
                  <p
                    className={cn("px-4 pt-2.5 text-xs text-muted-foreground", !chips && "pb-2.5")}
                  >
                    {footerStart}
                  </p>
                )}
                {chips && (
                  <div
                    ref={chipRow}
                    onMouseDown={keepFocus}
                    className="flex gap-1.5 overflow-x-auto px-4 py-2.5 no-scrollbar fade-x-edges *:shrink-0"
                  >
                    <PickersReturnFocusTo to={returnFocus}>{chips}</PickersReturnFocusTo>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
        {/* It pushes no history entry of its own: the screen's entry, beneath, is taken off as the
            screen closes, and Back while it asks reaches the screen, which answers Cancel. */}
        <DestructiveConfirmation
          removes="unsaved-text"
          discards={discards}
          trigger="none"
          navigationBlocker
          open={asking}
          onOpenChange={setAsking}
          onConfirm={async () => onOpenChange(false)}
        />
      </>
    )

  return (
    <Dialog open={open} onOpenChange={(next) => onOpenChange(next)}>
      <DialogContent
        ref={popup}
        position="top"
        size="lg"
        showCloseButton={false}
        initialFocus={titleField}
      >
        <div className="flex h-11 items-center gap-2 px-4">
          <DialogTitle className="flex min-w-0 items-center gap-1.5 text-xs font-normal text-muted-foreground">
            {lead}
            <span className="truncate">{label}</span>
          </DialogTitle>
          <DialogClose
            render={
              <Button variant="ghost" size="icon-sm" className="ml-auto" aria-label="Close" />
            }
          >
            <XIcon aria-hidden />
          </DialogClose>
        </div>

        <div className="space-y-2 px-5 pb-2">
          {titled}
          {body && <div className="min-h-28 text-prose">{body}</div>}
          {chips && <div className="flex flex-wrap gap-1.5 pt-2">{chips}</div>}
        </div>

        <div className="flex h-12 items-center gap-2 border-t border-border-subtle px-4">
          {footerStart && (
            <p className="line-clamp-2 min-w-0 text-xs leading-4 text-muted-foreground">
              {footerStart}
            </p>
          )}
          <div className="ml-auto flex shrink-0 items-center gap-2">
            {createMore && (
              <label className="mr-1 flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
                <Switch checked={createMore.checked} onCheckedChange={createMore.onChange} />
                Create more
              </label>
            )}
            <DialogClose render={<Button variant="ghost" size="sm" />}>Cancel</DialogClose>
            <Button
              size="sm"
              disabled={!submittable}
              // Submitting, it keeps its colour and takes no second press.
              focusableWhenDisabled={pending}
              onClick={onSubmit}
            >
              {pending && <Loader2Icon aria-hidden className="animate-spin" />}
              {submitLabel}
              {!pending && <Kbd shortcut="submit" />}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

/** Keeps the focus where it is through a press, as a mousedown's default would move it. */
function keepFocus(event: { preventDefault: () => void }) {
  event.preventDefault()
}
